/**
 * IdGateWhyCard — plain-English explainer that sits inside the
 * /medications ID-verification gate. Tells the patient WHY both steps
 * (or just one, or neither) are being asked of them based on what
 * their state's telemedicine board requires.
 *
 * Pulls everything from the live status payload — no hard-coded state
 * lookup tables. When the admin adds/removes a per-state override in
 * System Ops → ID verification, the copy here updates automatically.
 *
 * Props:
 *   - status: { patient_state, state_override_active, require_id_card,
 *               require_video, has_id_card, has_video }
 */
import { Info } from "lucide-react";

const STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire",
  NJ: "New Jersey", NM: "New Mexico", NY: "New York", NC: "North Carolina",
  ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee",
  TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming", DC: "District of Columbia",
};

function describe({ require_id_card, require_video }) {
  if (require_id_card || require_video) {
    return "asks GLPFlo to verify your identity — a photo of your government-issued ID (driver's license, state ID, or passport) plus the ID details — before a prescription can be issued.";
  }
  return "doesn't require identity verification. You're free to proceed straight to picking a medication.";
}

export default function IdGateWhyCard({ status }) {
  if (!status) return null;
  const stateCode = status.patient_state;
  const stateName = stateCode ? (STATE_NAMES[stateCode] || stateCode) : null;
  const usingOverride = !!status.state_override_active;

  // Lead sentence uses the patient's actual state if we know it,
  // otherwise falls back to "your state".
  const stateLead = stateName
    ? `${stateName}'s telemedicine board`
    : "Your state's telemedicine board";

  return (
    <div data-testid="id-gate-why-card"
         className="rounded-2xl border border-slate-200 bg-white p-5 mb-6">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-purple-100 p-2 shrink-0">
          <Info className="w-5 h-5 text-purple-700" />
        </div>
        <div className="flex-1 text-sm leading-relaxed text-slate-700">
          <h3 className="font-bold text-slate-900 mb-1">
            Why we ask for {stateName ? `this in ${stateName}` : "this"}
          </h3>
          <p>
            {stateLead} {describe(status)}
            {usingOverride && (
              <> {" "}
                <span className="text-purple-700 font-semibold">
                  This is set specifically for {stateName || "your state"}
                </span>{" "}
                — other states may have different requirements.
              </>
            )}
          </p>
          <p className="mt-2 text-xs text-slate-500">
            You&apos;ll upload a photo of your government-issued ID and enter its details.
            Your prescriber verifies the ID during prescription review — you can continue through intake and checkout as soon as it&apos;s uploaded.
          </p>
        </div>
      </div>
    </div>
  );
}
